import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { inject, linkedSignal } from '@angular/core';
import {
  concatMap,
  exhaustMap,
  filter,
  pipe,
  tap,
  combineLatest,
  take,
} from 'rxjs';
import {
  withRequestStatus,
  setReadStatus,
  setCreateStatus,
  setUpdateStatus,
} from '../../../../shared/store-utils/request-status.feature';
import { withErrorHandler } from '../../../../shared/store-utils/error-handler.feature';
import { tapResponse } from '@ngrx/operators';
import { UsersManagementService } from '../services';
import { IInviteUser, IProfileEdit, IUser } from '../interfaces';
import { MatDialogRef } from '@angular/material/dialog';
import {
  createTempUser,
  filterUsers,
  isNameDuplicate,
  parsePendingUser,
  parsePendingUsers,
  parseUpdatedRole,
  parseUpdatedUser,
  updateRoleInTable,
  updateUser,
} from '../users-management.helpers';
import { AddUserDialogComponent } from '../components/add-user-dialog/add-user-dialog.component';
import { EmailValidators } from '../../../../shared/validators';
import { ProfileEditComponent } from '../components/profile-edit/profile-edit.component';
import { SharedUIService } from '../../../../shared/services/ui-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  SNACKBAR_DELETE_MESSAGE,
  SNACKBAR_UPDATE_MESSAGE,
  UNDO_CLICKED,
} from '../../../../shared/constants';
import { IRole } from '../../roles-management/interfaces';
import {
  setAllEntities,
  setEntities,
  withEntities,
  setEntity,
  addEntity,
  removeEntity,
} from '@ngrx/signals/entities';
import { withUiStates } from '../../../../shared/store-utils/ui-state.feature';
import { withCRUD } from '../../../../shared/store-utils/crud.feature';
import {
  addUserDialogDescription,
  repeatedNameDialogData,
  addUserSentEmailData,
  deleteUserDialogData,
} from '../constants';
import { withDevtools, withUndoRedo } from '@angular-architects/ngrx-toolkit';
import { ChangePasswordComponent } from '../../change-password/components/change-password.component';
import { withUtils } from '../../../../shared/store-utils/utils.feature';
import { TranslationService } from '../../../shared/services/translation.service';
import {
  DELETE_DATA_TYPE,
  NEW_DATA_TYPE,
  UPDATE_DATA_TYPE,
} from '../../../../shared/services/data-sync/enums';
import { withDataSync } from '../../../../shared/store-utils/data-sync.feature';
import { StateStatus } from '../../../core/enums';

interface UsersManagementState {
  user: IUser | null;
  newUser: IInviteUser | null;
  pendingUsers: IUser[];
  tempUser: IUser | null;
  tempRole: Partial<IRole> | null;
  profileModal: MatDialogRef<ProfileEditComponent> | null;
  test: string[];
}

const initialState: UsersManagementState = {
  user: null,
  newUser: null,
  pendingUsers: [],
  tempUser: null,
  tempRole: null,
  profileModal: null,
  test: [],
};

export const UsersManagementStore = signalStore(
  { providedIn: 'root' },
  withDevtools('UsersManagementStore'),
  withState(initialState),
  withEntities<IUser>(),
  withRequestStatus(),
  withErrorHandler(),
  withUiStates(),
  withCRUD<IUser>(),
  withUtils<IUser>(),
  withUndoRedo(),
  withProps(() => ({
    _userManagementService: inject(UsersManagementService),
    _emailValidator: inject(EmailValidators),
    _sharedUIService: inject(SharedUIService),
    _translationService: inject(TranslationService),
  })),
  withComputed(({ entities, pendingUsers, searchQuery, roleFilter }) => {
    const filteredUsers = linkedSignal(() => {
      const allUsers = [...pendingUsers(), ...entities()];
      let filtered = allUsers;

      if (searchQuery()) {
        filtered = filterUsers(filtered, searchQuery());
      }

      if (roleFilter().length > 0) {
        filtered = filtered.filter((user) =>
          roleFilter().some((role) => role === user.userRole.id),
        );
      }
      return filtered;
    });
    return {
      filteredUsers,
    };
  }),
  withMethods((store) => {
    function setTempUser(user: IUser | null): void {
      patchState(store, { tempUser: user ? createTempUser(user) : null });
    }

    function checkUniqueName(
      fullName: string,
      updatedUser: IProfileEdit,
      dialogRef?: MatDialogRef<ProfileEditComponent>,
    ): void {
      const isDuplicate = isNameDuplicate(
        store.entities(),
        fullName,
        store.tempUser()!.id,
      );
      if (isDuplicate)
        store._sharedUIService.showConfirmation(
          repeatedNameDialogData,
          () => {},
        );

      if (!isDuplicate && dialogRef) dialogRef.close(updatedUser);
      if (!isDuplicate && !dialogRef) selfUserUpdate(updatedUser);
    }

    function resetState() {
      patchState(store, {
        searchQuery: '',
      });
    }

    function openChangePasswordDialog(
      dialogRef: MatDialogRef<ProfileEditComponent>,
    ) {
      dialogRef?.close();
      store._sharedUIService.openDialog(ChangePasswordComponent, {
        panelClass: 'change-password-dialog',
        disableClose: true,
      });
    }

    function upsert(user: IUser) {
      patchState(store, setEntity(user));
    }

    function getRequiredTranslations() {
      return combineLatest({
        description: store._translationService.getTranslation(
          addUserDialogDescription,
        ),
        emailSentTitle: store._translationService.getTranslation(
          addUserSentEmailData.title,
        ),
        emailSentMessage: store._translationService.getTranslation(
          addUserSentEmailData.message,
        ),
      }).pipe(take(1));
    }
    
    const loadUsers = store.loadRxMethod()(
      () => store._userManagementService.getUsers(),
      {
        beforeLoad: () => patchState(store, setReadStatus(StateStatus.PENDING)),
      },
    );

    const deleteUser = store.deleteRxMethod<IUser>()(
      (id) => store._userManagementService.deleteUser(id),
      SNACKBAR_DELETE_MESSAGE,
      deleteUserDialogData,
      undefined,
      (user) => patchState(store, setEntities([user])),
    );

    const addUserDialog = rxMethod<void>(
      pipe(
        exhaustMap(() =>
          getRequiredTranslations().pipe(
            exhaustMap((translations) =>
              store._sharedUIService
                .openDialog(AddUserDialogComponent, {
                  data: {
                    description: translations.description,
                    customValidator: store._emailValidator.emailExists([
                      ...store.entities(),
                      ...store.pendingUsers(),
                    ]),
                  },
                })
                .afterClosed()
                .pipe(
                  filter((result) => !!result),
                  tap(() => patchState(store, setCreateStatus(StateStatus.PENDING))),
                  concatMap((result) =>
                    store._userManagementService.inviteUser(result).pipe(
                      tapResponse({
                        next: (user) => {
                          patchState(
                            store,
                            {
                              pendingUsers: [
                                parsePendingUser(user, user.id),
                                ...store.pendingUsers(),
                              ],
                            },
                            setCreateStatus(StateStatus.FULFILLED),
                          );
                          store._sharedUIService.openSuccessDialog({
                            icon: 'mark_email_read',
                            title: translations.emailSentTitle,
                            boldMessage: user.email,
                            message: translations.emailSentMessage,
                            action: 'ok',
                          });
                        },
                        error: (error: { message: string }) =>
                          patchState(store, setCreateStatus({ error: error.message })),
                      }),
                    ),
                  ),
                ),
            ),
          ),
        ),
      ),
    );

    const loadPendingUsers = rxMethod<void>(
      pipe(
        tap(() => patchState(store, setReadStatus(StateStatus.PENDING))),
        exhaustMap(() =>
          store._userManagementService.getPendingUsers().pipe(
            tapResponse({
              next: (pendingUsers) =>
                patchState(
                  store,
                  { pendingUsers: parsePendingUsers(pendingUsers) },
                  setReadStatus(StateStatus.FULFILLED),
                ),
              error: (error: { message: string }) =>
                patchState(store, setReadStatus({error: error.message})),
            }),
          ),
        ),
      ),
    );

    const updateRoleFromTable = rxMethod<{
      userId: string;
      role: IRole;
    }>(
      pipe(
        tap((updateRole) => {
          patchState(
            store,
            setUpdateStatus(StateStatus.PENDING),
            setAllEntities(parseUpdatedRole(updateRole, store.entities())),
            {
              tempRole: store.entities().find((u) => u.id === updateRole.userId)
                ?.userRole,
            },
          );
        }),
        exhaustMap((updateRole) => {
          const updateUser = store
            .entities()
            .find((u) => u.id === updateRole.userId);

          return store._sharedUIService
            .withUndoRequestAction(
              store._userManagementService.updateUser(updateRole.userId, {
                userRole: updateRole.role,
              }),
              SNACKBAR_UPDATE_MESSAGE,
            )
            .pipe(
              tapResponse({
                next: (result: IUser | typeof UNDO_CLICKED) => {
                  if (result === UNDO_CLICKED) {
                    patchState(
                      store,
                      setAllEntities(
                        updateRoleInTable(
                          store.entities(),
                          updateUser!,
                          store.tempRole()!,
                        ),
                      ),
                      setUpdateStatus(StateStatus.FULFILLED),
                    );
                  } else {
                    patchState(store, setUpdateStatus(StateStatus.FULFILLED));
                  }
                },
                error: (error: HttpErrorResponse) =>
                  patchState(
                    store,
                    setAllEntities(
                      updateRoleInTable(
                        store.entities(),
                        updateUser!,
                        store.tempRole()!,
                      ),
                    ),
                    setUpdateStatus({ error: error.message }),
                  ),
              }),
            );
        }),
      ),
    );

    const openEditProfileModal = rxMethod<{ user: IUser; roles: IRole[] }>(
      pipe(
        tap((data) => patchState(store, { tempUser: data.user })),
        exhaustMap(() => {
          return store.runDialogWorkflow(store._sharedUIService, {
            dialogComponent: ProfileEditComponent,
            dialogConfig: {
              panelClass: 'profile-dialog',
            },
            onBeforeServiceCall: (result: IProfileEdit) => {
              patchState(
                store,
                setAllEntities(
                  updateUser(
                    {
                      userId: store.tempUser()!.id,
                      profileEdit: result,
                      userRole: result.userRole!,
                    },
                    store.entities(),
                  ),
                ),
              );
            },

            getServiceCall: (result: IProfileEdit) =>
              store._userManagementService.updateUser(
                store.tempUser()!.id,
                result,
              ),
            onSuccess: () => {
              patchState(store, setUpdateStatus(StateStatus.FULFILLED));
            },
            withUndo: true,
            undoMessage: SNACKBAR_UPDATE_MESSAGE,
          });
        }),
      ),
    );
    //TODO: switch for undo snackbar when current user logic is removed from auth store
    const selfUserUpdate = rxMethod<IProfileEdit>(
      pipe(
        tap(() => patchState(store, setUpdateStatus(StateStatus.PENDING))),
        exhaustMap((updatedUser) =>
          store._userManagementService
            .updateUser(store.tempUser()!.id, updatedUser)
            .pipe(
              tap(() => {
                store._sharedUIService.withCloseAction(SNACKBAR_UPDATE_MESSAGE);
              }),
              tapResponse({
                next: (updatedUser) =>
                  patchState(
                    store,
                    setAllEntities(
                      parseUpdatedUser(store.entities(), updatedUser),
                    ),
                    setUpdateStatus(StateStatus.FULFILLED),
                  ),
                error: (error: { message: string }) =>
                  patchState(store, setUpdateStatus({ error: error.message })),
              }),
            ),
        ),
      ),
    );

    return {
      loadUsers,
      deleteUser,
      addUserDialog,
      loadPendingUsers,
      setTempUser,
      openEditProfileModal,
      checkUniqueName,
      updateRoleFromTable,
      resetState,
      openChangePasswordDialog,
      selfUserUpdate,
      upsert,
    };
  }),
  withDataSync({
    newDataTypes: {
      [NEW_DATA_TYPE.ROLE]: (data, store) => {
        const role = data.role;
        const assignedUserIds = new Set(role.assignedUsers);
        const allUsers = store.entities();
        if (assignedUserIds.size === 0) return;

        const updatedEntities = allUsers.map((user: IUser) => {
          if (assignedUserIds.has(user.id)) {
            return {
              ...user,
              userRole: role,
            };
          }
          return user;
        });

        patchState(store, setAllEntities(updatedEntities));
      },
      [NEW_DATA_TYPE.USERINVITE]: (data, store) => {
        patchState(store, addEntity(parsePendingUser(data, data.userId)));
      },
    },
    updateDataTypes: {
      [UPDATE_DATA_TYPE.USER_UPDATED]: (data, store) => {
        const updatedUser = data.user;
        if (updatedUser) {
          const updatedEntities = parseUpdatedUser(
            store.entities(),
            updatedUser,
          );
          patchState(store, setAllEntities(updatedEntities));
        }
      },
      [UPDATE_DATA_TYPE.ROLE_UPDATED]: (data, store) => {
        const updatedRole = data.role;
        if (updatedRole) {
          updatedRole.assignedUsers.forEach((userId: string) => {
            const user = store.entities().find((u: IUser) => u.id === userId);
            if (user) {
              patchState(
                store,
                setEntity({
                  ...user,
                  userRole: updatedRole,
                }),
              );
            }
          });
        }
      },
    },
    deleteDataTypes: {
      [DELETE_DATA_TYPE.USER]: (data, store) => {
        const deletedUserId = data.userId;
        if (deletedUserId) {
          patchState(store, removeEntity(deletedUserId));
        }
      },
    },
  }),
  withHooks({
    onInit({ loadUsers, loadPendingUsers, initializeDataSync }) {
      loadUsers();
      loadPendingUsers();
      initializeDataSync();
    },
  }),
);
